import { Flex } from 'antd';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router';

import { DownloadButton } from '@/pages/UserAccount/UserRequests/ui';

import { StepProps } from '@/widgets/ServiceFormTemplate';

import { ArtifactType, InvoiceStatus, UserRequestStatus } from '@/shared/types';
import { Button, ButtonSizes, ButtonVariant, ConditionView, LinkAsButton, StepControlPanel } from '@/shared/ui';

import { Result } from '@/components/Forms/Steps/Result';

import { useArtifactLoader } from '@/hooks/useArtifactLoader';
import { Breakpoints, useMediaQuery } from '@/hooks/useMediaQuery';
import { useShortPolling } from '@/hooks/useShortPolling';

import { ROUTES } from '@/router/config';

import { UserRequest } from '@/api/types';

import api from '@/api';

export interface InsuranceServiceResultProps {
  finishedUserRequestStatus: boolean;
  invoiceStatus: Nullable<InvoiceStatus>;
  userRequestId: UserRequest['id'];
  redirectServiceInfo: Nullable<{
    link: string;
    request_id: number;
  }>;
}

export const InsuranceServiceResult: React.FC<StepProps<InsuranceServiceResultProps>> = ({
  userRequestId,
  serviceCode,
  finishedUserRequestStatus,
  invoiceStatus,
  redirectServiceInfo,
}) => {
  const navigate = useNavigate();
  const isMobileSize = useMediaQuery(Breakpoints.SM);
  const { isArtifactLoading, currentArtifactTypeLoading, downloadArtifact } = useArtifactLoader((artifactType) =>
    api.downloadUserRequestArtifact(userRequestId, artifactType),
  );
  const { isShortPollingSuccessed: isUserRequestFinished, startShortPolling: checkIsUserRequestFinished } =
    useShortPolling({
      delayTime: 1000 * 15, // 15sec
      totalAwaitTime: 1000 * 180, // 3min
      fetchFunction: async () => {
        const { data: userRequest } = await api.services.draft.getDetails(userRequestId);

        return userRequest;
      },
      isResponseAppropriate: ({ status }) => {
        return status === UserRequestStatus.FINISHED;
      },
    });

  useEffect(() => {
    invoiceStatus === InvoiceStatus.SUCCESS && checkIsUserRequestFinished();
  }, [invoiceStatus]);

  return (
    <>
      <Result
        variant="new"
        serviceCode={serviceCode}
        finishedUserRequestStatus={finishedUserRequestStatus}
        invoiceStatus={invoiceStatus}
      />

      <ConditionView
        if={invoiceStatus === InvoiceStatus.SUCCESS}
        else={<StepControlPanel next={() => navigate(ROUTES.HOME)} nextBtnText="На головну" withArrow={false} />}
      >
        <StepControlPanel>
          <Flex justify="space-between" vertical={isMobileSize} gap={8} align="center">
            <Flex gap={8} vertical={isMobileSize} style={{ width: isMobileSize ? '100%' : 'fit-content' }}>
              <ConditionView if={isUserRequestFinished}>
                <DownloadButton
                  size={ButtonSizes.LG}
                  serviceCode={serviceCode}
                  iconName="download"
                  buttonVariant={ButtonVariant.SECONDARY}
                  type={ArtifactType.INSURANCE}
                  disabled={isArtifactLoading && currentArtifactTypeLoading === ArtifactType.INSURANCE}
                  onClick={() => downloadArtifact(ArtifactType.INSURANCE, serviceCode)}
                />
              </ConditionView>
              <DownloadButton
                size={ButtonSizes.LG}
                serviceCode={serviceCode}
                iconName="download"
                buttonVariant={ButtonVariant.SECONDARY}
                type={ArtifactType.RECEIPT}
                disabled={isArtifactLoading && currentArtifactTypeLoading === ArtifactType.RECEIPT}
                onClick={() => downloadArtifact(ArtifactType.RECEIPT, serviceCode)}
              />
            </Flex>

            <ConditionView
              if={!isEmpty(redirectServiceInfo)}
              else={
                <LinkAsButton style={{ width: isMobileSize ? '100%' : 'fit-content' }} to={ROUTES.HOME}>
                  На головну
                </LinkAsButton>
              }
            >
              <Button
                style={{ width: isMobileSize ? '100%' : 'fit-content' }}
                disabled={!isUserRequestFinished}
                onClick={() =>
                  navigate(redirectServiceInfo!.link, {
                    state: {
                      continueDraft: true,
                      request_id: redirectServiceInfo!.request_id,
                    },
                  })
                }
              >
                Продовжити заповнення послуги
              </Button>
            </ConditionView>
          </Flex>
        </StepControlPanel>
      </ConditionView>
    </>
  );
};
